/**
 * @brief 按 answerMode 路由一轮回答
 * @note 由 handler.ts 的 SUBTITLE final 事件调起（仅当说话人是本地用户）
 */

import store from '@/store';
import RtcClient from '@/lib/RtcClient';
import Apis from '@/app/index';
import { COMMAND, INTERRUPT_PRIORITY } from '@/utils/handler';
import { setPendingBroadcastText } from '@/store/slices/room';
import logger from '@/utils/logger';
import type { ChatMessage } from '@/app/Llm';

const interrupt = (botName: string) => {
  RtcClient.commandAgent({
    agentName: botName,
    command: COMMAND.INTERRUPT,
    interruptMode: INTERRUPT_PRIORITY.HIGH,
  });
};

const speak = (botName: string, text: string) => {
  RtcClient.commandAgent({
    agentName: botName,
    command: COMMAND.EXTERNAL_TEXT_TO_SPEECH,
    interruptMode: INTERRUPT_PRIORITY.HIGH,
    message: text,
  });
};

/**
 * @brief 把 Redux msgHistory 转成 LLM 聊天记录
 */
const buildMessages = (utterance: string): ChatMessage[] => {
  const state = store.getState().room;
  const botName = state.sceneConfigMap[state.scene]?.botName;
  const localUserId = state.localUser.userId;

  const history: ChatMessage[] = [];
  state.msgHistory.forEach((m) => {
    if (!m.value || !m.value.trim()) return;
    const isBot = m.user === botName || m.user.includes('voiceChat_');
    const isLocal = m.user === localUserId;
    if (isBot) history.push({ role: 'assistant', content: m.value });
    else if (isLocal) history.push({ role: 'user', content: m.value });
  });

  // 去除末尾与本次 utterance 重复的用户消息（setHistoryMsg 已先写入）
  const last = history[history.length - 1];
  if (last && last.role === 'user' && last.content === utterance) {
    history.pop();
  }
  history.push({ role: 'user', content: utterance });
  return history;
};

let inFlight = false;

export const onUserUtteranceFinal = async (utterance: string) => {
  if (!utterance || !utterance.trim()) return;
  if (inFlight) {
    logger.debug('[turnRouter] 上一轮尚未结束，丢弃 utterance');
    return;
  }
  const state = store.getState().room;
  const { answerMode } = state;
  const botName = state.sceneConfigMap[state.scene]?.botName;
  if (!botName) return;

  if (answerMode === 'doubao-llm') {
    return;
  }

  inFlight = true;
  try {
    if (answerMode === 'text-broadcast') {
      interrupt(botName);
      const text = state.pendingBroadcastText;
      if (!text) {
        logger.debug('[turnRouter] text-broadcast 模式无预置文本，跳过');
        return;
      }
      speak(botName, text);
      store.dispatch(setPendingBroadcastText(null));
      return;
    }

    if (answerMode === 'external-llm') {
      interrupt(botName);
      const { provider, model, baseURL, apiKey, systemPrompt } = state.externalLlm;
      const messages = buildMessages(utterance);
      const reply = await Apis.Llm.chat({
        provider,
        model,
        baseURL,
        apiKey,
        systemPrompt,
        messages,
      });
      const text = reply.text?.trim();
      if (!text) {
        logger.debug('[turnRouter] external-llm 返回空');
        return;
      }
      speak(botName, text);
    }
  } catch (e) {
    logger.debug('[turnRouter] 路由失败', e);
  } finally {
    inFlight = false;
  }
};
